import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ChevronLeft,
  Plus,
  FileText,
  Settings,
  Download,
  ChevronDown,
  FolderOpen,
  Printer,
  FileSpreadsheet,
  HelpCircle,
  Globe,
  Upload,
  Users,
  Building2,
  UserCircle,
  Cpu
} from 'lucide-react';
import ImportDialog from './ImportDialog';

const Header = ({ activeTab, setActiveTab, onNavigateToOverview }) => {
  const { currentProject, projects, selectProject, createProject, customers } = useProject();
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectAddress, setNewProjectAddress] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Vereinfachte Tabs - ohne separaten Stromlaufplan-Tab (ist jetzt in Planung integriert)
  const mainTabs = [
    { id: 'allgemein', label: 'Allgemein' },
    { id: 'planung', label: 'Planung' },
    { id: 'exporte', label: 'Exporte' },
    { id: 'verteilerbau', label: 'Verteilerbau' }
  ];

  // Gebäude des ausgewählten Kunden
  const selectedCustomer = customers?.find(c => c.id === selectedCustomerId);
  const availableBuildings = selectedCustomer?.buildings || [];

  const handleCreateProject = () => {
    if (newProjectName.trim()) {
      const customerId = selectedCustomerId && selectedCustomerId !== 'none' ? selectedCustomerId : null;
      const buildingId = selectedBuildingId && selectedBuildingId !== 'none' ? selectedBuildingId : null;
      createProject(
        newProjectName.trim(), 
        newProjectAddress.trim(),
        customerId,
        buildingId
      );
      setNewProjectName('');
      setNewProjectAddress('');
      setSelectedCustomerId('');
      setSelectedBuildingId('');
      setIsNewProjectOpen(false);
    }
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    setSelectedBuildingId(''); // Reset building when customer changes
  };

  const handleBackClick = () => {
    if (onNavigateToOverview) {
      onNavigateToOverview();
    }
  };

  return (
    <header className="bg-white border-b border-gray-200">
      {/* Top Bar - Grüne Leiste */}
      <div className="bg-emerald-700 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-lg">StromlaufplanPro</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Projekte Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-white hover:bg-emerald-600">
                <FileText className="w-4 h-4 mr-2" />
                Projekte
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              {projects.map((project) => (
                <DropdownMenuItem
                  key={project.id}
                  onClick={() => selectProject(project.id)}
                  className={project.id === currentProject?.id ? 'bg-sky-50' : ''}
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  <div className="flex-1 truncate">
                    <div className="font-medium truncate">{project.name}</div>
                    <div className="text-xs text-gray-500 truncate">{project.address}</div>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <Dialog open={isNewProjectOpen} onOpenChange={setIsNewProjectOpen}>
                <DialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Neues Projekt
                  </DropdownMenuItem>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Neues Projekt erstellen</DialogTitle>
                    <DialogDescription>
                      Geben Sie die Projektdaten ein, um ein neues Projekt zu erstellen.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Projektname *</Label>
                      <Input
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        placeholder="z.B. Hauptverteiler EG"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Adresse</Label>
                      <Input
                        value={newProjectAddress}
                        onChange={(e) => setNewProjectAddress(e.target.value)}
                        placeholder="z.B. Musterstraße 1, 12345 Musterstadt"
                      />
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm text-gray-500 mb-3">Optional: Projekt einem Kunden/Gebäude zuordnen</p>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label>Kunde</Label>
                          <Select value={selectedCustomerId} onValueChange={handleCustomerChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Kunde auswählen (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Kein Kunde</SelectItem>
                              {(customers || []).map((customer) => (
                                <SelectItem key={customer.id} value={customer.id}>
                                  {customer.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {selectedCustomerId && selectedCustomerId !== 'none' && (
                          <div className="space-y-2">
                            <Label>Gebäude</Label>
                            <Select value={selectedBuildingId} onValueChange={setSelectedBuildingId}>
                              <SelectTrigger>
                                <SelectValue placeholder="Gebäude auswählen (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Kein Gebäude</SelectItem>
                                {availableBuildings.map((building) => (
                                  <SelectItem key={building.id} value={building.id}>
                                    {building.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsNewProjectOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button onClick={handleCreateProject} disabled={!newProjectName.trim()}>
                      Erstellen
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <DropdownMenuItem onSelect={(e) => {
                e.preventDefault();
                setIsImportOpen(true);
              }}>
                <Upload className="w-4 h-4 mr-2" />
                Von stromlaufplan.de importieren
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ImportDialog open={isImportOpen} onOpenChange={setIsImportOpen} />

          {/* Aktuelles Projekt als Bubble (wenn ausgewählt) */}
          {currentProject && (
            <>
              <div className="w-px h-6 bg-emerald-500 mx-1" />
              <div 
                className="flex items-center gap-2 px-3 py-1 bg-emerald-600 rounded-full cursor-pointer hover:bg-emerald-500 transition-colors"
                onClick={handleBackClick}
                title="Zur Projektübersicht"
              >
                <FileText className="w-3.5 h-3.5 text-emerald-200" />
                <span className="text-sm text-white font-medium max-w-[200px] truncate">
                  {currentProject.name}
                </span>
                <ChevronLeft className="w-3.5 h-3.5 text-emerald-200" />
              </div>
            </>
          )}

          <div className="flex-1" /> {/* Spacer */}

          {/* Kunden */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-emerald-600"
            onClick={() => onNavigateToOverview && onNavigateToOverview('kunden')}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Kunden
          </Button>

          {/* Mitarbeiter */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-emerald-600"
            onClick={() => onNavigateToOverview && onNavigateToOverview('mitarbeiter')}
          >
            <Users className="w-4 h-4 mr-2" />
            Mitarbeiter
          </Button>

          {/* Geräte */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-emerald-600"
            onClick={() => setActiveTab('geraete')}
          >
            <Cpu className="w-4 h-4 mr-2" />
            Geräte
          </Button>

          {/* Einstellungen */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-emerald-600"
            onClick={() => setActiveTab('einstellungen')}
          >
            <Settings className="w-4 h-4 mr-2" />
            Einstellungen
          </Button>

          <div className="w-px h-6 bg-emerald-500 mx-1" />

          {/* Anleitung */}
          <Button variant="ghost" size="sm" className="text-white hover:bg-emerald-600">
            <HelpCircle className="w-4 h-4 mr-2" />
            Anleitung
          </Button>

          {/* Sprache */}
          <Button variant="ghost" size="sm" className="text-white hover:bg-emerald-600">
            <Globe className="w-4 h-4 mr-2" />
            Sprache
          </Button>
        </div>
      </div>

      {/* Main Tabs - direkt unter dem Header (ohne extra Projekt-Leiste) */}
      {currentProject && (
        <div className="px-4 border-b border-gray-200 bg-white">
          <nav className="flex gap-1">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'text-emerald-600 border-emerald-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
